import { Accordion, useAccordion } from '@ark-ui/react';
import cn from 'classnames';
import { useState } from 'react';

import { useDictionary } from 'src/hooks/useDictionary';

import { CoreServicesListingTabsData } from 'src/types/components';

import ArrowForward from 'assets/icons/arrow-forward.svg';
import CaretUp from 'assets/icons/caret-up.svg';

import InlineIconText from 'components/BaseHelpers/InlineIconText';
import Link from 'components/BaseHelpers/Link';
import Text from 'components/BaseHelpers/Text';

import styles from './ServicesList.module.scss';

type Service = CoreServicesListingTabsData['fields']['tabs'][number]['services'][number];

type ListingProps = ServicesListProps & {
  mobile: boolean;
};

const Listing = (props: ListingProps): JSX.Element => {
  const { list, mobile } = props;
  const [value, setValue] = useState<string[]>([]);
  const { expandAllLabel, collapseAllLabel } = useDictionary();

  const accordionContext = useAccordion({
    multiple: true,
    value,
    onValueChange: (value) => {
      setValue(value.value);
    },
  });

  const servicesWithChildren = list
    .filter((item) => Array.isArray(item.children) && item.children.length > 0)
    .map((service) => service.url);
  const areAllExpanded = servicesWithChildren.length === value.length;
  const handleExpandAll = () => (areAllExpanded ? setValue([]) : setValue(servicesWithChildren));

  const getList = (services: Service[]) => {
    return (
      <ul className={cn(styles.list)}>
        {services.map((service) => {
          if (Array.isArray(service.children) && service.children.length > 0) {
            return (
              <li key={service.url}>
                <Accordion.Item className={styles.accordion} value={service.url}>
                  <Accordion.ItemTrigger className={cn(styles.trigger, 'heading-h4')}>
                    <Text field={service.name} />
                    <Accordion.ItemIndicator className={styles.indicator}>
                      <CaretUp className={styles['accordion-trigger-icon']} />
                    </Accordion.ItemIndicator>
                  </Accordion.ItemTrigger>
                  <Accordion.ItemContent className={styles.content}>
                    <ul className={styles['child-items']}>
                      <li>
                        <Link
                          href={service.url}
                          className={cn(
                            styles['child-item-link'],
                            styles['child-item-parent-link'],
                            'body-lg'
                          )}
                        >
                          <InlineIconText
                            field={service.name}
                            icon={<ArrowForward className={cn(styles['child-item-link-icon'])} />}
                          />
                        </Link>
                      </li>
                      {service.children.map((child) => (
                        <li key={child.url} className={styles['child-item']}>
                          <Link
                            href={child.url}
                            className={cn(styles['child-item-link'], 'body-sm')}
                          >
                            <InlineIconText
                              field={child.name}
                              icon={<ArrowForward className={cn(styles['child-item-link-icon'])} />}
                            />
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </Accordion.ItemContent>
                </Accordion.Item>
              </li>
            );
          } else {
            return (
              <li key={service.url} className={styles['list-item']}>
                <Link href={service.url} className={cn(styles['list-item-link'], 'heading-h4')}>
                  <Text field={service.name} />
                  <ArrowForward className={styles['list-item-link-icon']} />
                </Link>
              </li>
            );
          }
        })}
      </ul>
    );
  };

  return (
    <div
      className={cn({
        [styles['mobile']]: mobile,
        [styles['desktop']]: !mobile,
      })}
    >
      <Accordion.RootProvider value={accordionContext}>
        {Boolean(servicesWithChildren.length) && (
          <button onClick={handleExpandAll} className={cn('body-sm', styles['expand-all-button'])}>
            <Text field={areAllExpanded ? collapseAllLabel : expandAllLabel} />
          </button>
        )}
        {mobile && getList(list)}
        {!mobile && (
          <div className={styles.grid}>
            {getList(list.filter((_, index) => index % 2 === 0))}
            {getList(list.filter((_, index) => index % 2 === 1))}
          </div>
        )}
      </Accordion.RootProvider>
    </div>
  );
};

export type ServicesListProps = {
  list: Service[];
};

const ServicesList = (props: ServicesListProps): JSX.Element => {
  return (
    <>
      <Listing {...props} mobile={true} />
      <Listing {...props} mobile={false} />
    </>
  );
};
export default ServicesList;
